import React, { useState } from "react";
import {
  toWSDateString,
  useWSModalCustom,
  WSControl,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInfoBox,
  WSList,
  WSModal,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import {
  ContextItem,
  RequirementAdditionalData,
  RequirementStatusViewProps
} from "../../types";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { queriesPayeeRows } from "@query";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { useMutationResetPayeeRequirement } from "../../../../query/requirements/mutations/useMutationResetPayeeRequirement";

interface ReRequestRequirementModalProps {
  onClose: () => void;
  requirement: IRequirementResponse;
  context: ContextItem;
  additionalData: RequirementAdditionalData;
  onSuccess: () => void;
  currentStatus: RequirementStatusViewProps;
}

type FormData = {
  reason: string;
};

const ReRequestRequirementModal: React.FC<ReRequestRequirementModalProps> = ({
  onClose,
  requirement,
  context,
  onSuccess,
  currentStatus
}) => {
  const [resetRequirement, resetRequirementMeta] =
    useMutationResetPayeeRequirement();

  const [resetExpiration, setResetExpiration] = useState(false);

  const xWingspanAccount = useXWingspanExpansion();
  const queryPayeeGeneralSummary = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        requirements_requirementId: { eq: requirement.requirementId }
      },
      page: { size: 1 },
      xWingspanAccount
    },
    { select: list => list.pagination?.totalSize }
  );

  const onSubmit = (formData: FormData) => {
    resetRequirement(
      {
        payeeId: requirement.payeeId,
        requirementId: requirement.requirementId
      },
      {
        onSuccess() {
          onSuccess();
          onClose();
        }
      }
    );
  };

  const contractorsLength = queryPayeeGeneralSummary.data || "-";

  return (
    <WSForm<FormData> onSubmit={onSubmit} defaultValues={{ reason: "" }}>
      <WSModal
        title="Re-request requirement"
        onClose={onClose}
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: onClose,
              type: "button"
            },
            {
              kind: "Primary",
              label: "Yes, re-request",
              disabled: requirement.expiresAt ? !resetExpiration : false,
              type: "submit",
              loading: resetRequirementMeta.isLoading
            }
          ]
        }}
      >
        <WSList gap="XL">
          <WSFlexBox gap="M" wrap="nowrap">
            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Current status:
              </WSText.ParagraphSm>
              <WSPill {...currentStatus.pill}></WSPill>
              <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
                {currentStatus.secondaryText}
              </WSText.ParagraphXs>
            </WSElement>
            <WSIcon
              style={{
                marginTop: "auto",
                marginBottom: "auto"
              }}
              name="arrow-right"
            />

            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Overwritten status preview:
              </WSText.ParagraphSm>
              <WSPill
                text="Awaiting contractor"
                theme="neutral"
                icon="time"
              ></WSPill>
              <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
                Awaiting contractor completion
              </WSText.ParagraphXs>
            </WSElement>
          </WSFlexBox>

          {requirement.expiresAt ? (
            <WSInfoBox py="L">
              <WSList gap="M">
                <WSElement>
                  <WSText.ParagraphXs color="gray500" mb="S">
                    Current expiration date
                  </WSText.ParagraphXs>
                  <WSText.ParagraphSm mt="XS" ml="S" color="gray600">
                    {toWSDateString(requirement.expiresAt, "input")}
                  </WSText.ParagraphSm>
                </WSElement>

                <WSText.ParagraphSm color="gray600">
                  This requirement has an expiration date extracted from the
                  document used to fulfill the requirement. By re-requesting, it
                  resets the requirement completely and removes the current
                  document and its expiration date. This action cannot be
                  reverted.
                  <br />
                  <br />
                  To retain the current expiration date, click “Cancel”. The
                  contractor is automatically reminded to renew their documents
                  starting 30 days before the expiration date.
                </WSText.ParagraphSm>
                <WSControl
                  type="checkbox"
                  value={resetExpiration}
                  onChange={setResetExpiration}
                  size="S"
                  label="Yes, I want to reset the requirement entirely and ignore the previous document and its expriration date."
                />
              </WSList>
            </WSInfoBox>
          ) : null}

          {/*
          <WSList gap="L">
            <WSElement>
              <WSForm.Field
                label="Comment (optional)"
                name="reason"
                component={WSInputTextarea}
                componentProps={{
                  helperText:
                    "Please provide specific details that will inform the contractor about the rejection",
                  placeholder: "Provide specific details about the decision"
                }}
              />
            </WSElement>
          </WSList>
          */}

          <WSInfoBox>
            <WSText.ParagraphSm color="gray600">
              By proceeding, the requirement status will be reset to incomplete.
              <ul>
                <li>
                  The status will be updated to “Awaiting contractor” (requiring
                  contractor action)
                </li>
                <li>
                  Provide a reason and comments to the contractor regarding this
                  decision
                </li>
                <li>
                  The decision and status updates will be tracked in the
                  requirement history
                </li>
              </ul>
            </WSText.ParagraphSm>
            <WSText.ParagraphSm color="gray600">
              This requirement is assigned to {contractorsLength} of the
              contractor’s engagements and may affect their eligibility to
              receive payments from you. Would you like to proceed?
            </WSText.ParagraphSm>
          </WSInfoBox>
          <WSErrorMessage
            mt="XL"
            contextKey="Other"
            error={resetRequirementMeta.isError}
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useReRequestRequirementModal = () =>
  useWSModalCustom(ReRequestRequirementModal);
